import {
  useFormContext,
  useWatch,
} from '@grnhse/seedling/lib/birch/components/forms/form';
import type { SearchFormData, TalentSearchQueryParams } from './types';
import queryParams, { toQueryString } from '../lib/query_params';
import { useQuery } from '@tanstack/react-query';
import Api from '../lib/api';
import { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';

type Props = {
  setFilteredCount: (count: number) => void;
  apiClient: Api;
  setLoading: (loading: boolean) => void;
  shouldCache?: boolean;
  defaultDirtyFields?: string[];
};

const loadCounts = async (params: TalentSearchQueryParams, apiClient: Api) =>
  apiClient.fetchCounts(params);

const FormWatcher = ({
  setFilteredCount,
  apiClient,
  setLoading,
  shouldCache = true,
  defaultDirtyFields = [],
}: Props) => {
  const [cacheable, setCacheable] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const {
    control,
    formState: { dirtyFields },
  } = useFormContext();
  const formValues = useWatch({ control });
  const params = queryParams(formValues as SearchFormData);
  const queryString = toQueryString(params);
  const [debouncedQueryString, setDebouncedQueryString] = useState(queryString);
  const [caching, setCaching] = useState(false);

  // This is used to correctly set the loading state for the initial load.
  // Since we set default filters immediately after the form is mounted, we need
  // to wait for those fields to be dirty before we set the initial loading state to false.
  // Otherwise, the loading state will flicker unnecessarily.
  useDebounce(
    () => {
      if (!initialLoad) {
        return;
      }

      if (
        defaultDirtyFields.every((field) => {
          return dirtyFields[field];
        })
      ) {
        setInitialLoad(false);
      }
    },
    200,
    [defaultDirtyFields, dirtyFields, setInitialLoad, initialLoad]
  );

  useDebounce(
    () => {
      setDebouncedQueryString(queryString);
    },
    200,
    [queryString]
  );

  const { data, isFetching } = useQuery({
    queryKey: [debouncedQueryString],
    queryFn: () => loadCounts(params, apiClient),
  });

  useEffect(() => {
    setLoading(isFetching || caching || initialLoad);
  }, [isFetching, caching, initialLoad, setLoading]);

  if (data?.count !== undefined) {
    setFilteredCount(data.count);
  }

  useDebounce(
    () => {
      if (!shouldCache) {
        return;
      }

      if (cacheable) {
        setCaching(true);
        apiClient.cacheParams(params).then(() => {
          setCaching(false);
        });
      } else {
        setCacheable(true);
      }
    },
    100,
    [debouncedQueryString]
  );

  return null;
};

export default FormWatcher;
